import {
  Box,
  Button,
  Divider,
  Group,
  Radio,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TagsInput,
  Transition,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconWorld, IconUsers } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';

import { VoiceSelector } from '@mygpt/app/chat/components/VoiceSelector';
import {
  GEMINI_REALTIME_MODELS,
  GEMINI_THINKING_MODELS,
  OPENAI_LIVE_MODELS,
  OPENAI_LIVE_VOICE_PROMPT_MAX_LEN,
  OPENAI_REALTIME_MODELS,
  OPENAI_REALTIME_REASONING_MODELS,
  OPENAI_REASONING_EFFORT_VALUES,
  OPENAI_REALTIME_TOOL_MODES,
  REALTIME_LANGUAGE_MODES,
  REALTIME_DEFAULT_VOICE_MAP,
  REALTIME_PROVIDER_LABEL,
  REALTIME_VOICE_OPTIONS,
  RealtimeProvider,
} from '@mygpt/app/chat/types/realtime';
import { BetaBadge } from '@/components/Common/BetaBadge';
import { DEFAULT_OPENAI_LIVE_VOICE_PROMPT } from './defaultLiveVoicePrompt';
import type { RealtimeFormValues } from './index';

interface Props {
  form: UseFormReturnType<RealtimeFormValues>;
  onValueChange: (value: boolean, emailList?: string[]) => void;
  onProviderChange: (value: string | null) => void;
  onOpenAIModelChange: (value: string | null) => void;
  onOpenAILiveVoicePromptChange: (value: string) => void;
  onOpenAIReasoningEffortChange: (value: string | null) => void;
  onOpenAIToolModeChange: (value: string | null) => void;
  onLanguageModeChange: (value: string | null) => void;
  onGeminiConfigChange: (
    key: 'gemini_realtime_model' | 'voice_thinking_level' | 'voice_tool_mode',
    value: string
  ) => void;
  onDefaultVoiceChange: (provider: RealtimeProvider, voice: string) => void;
  onEnableConsentChange: (value: boolean) => void;
  onInputUserIdentifierChange: (value: boolean) => void;
  onEnableSessionPromptChange: (value: boolean) => void;
}

const isNonProduction = import.meta.env.VITE_STAGE !== 'production';

export const AccessControl = ({
  form,
  onValueChange,
  onProviderChange,
  onOpenAIModelChange,
  onOpenAILiveVoicePromptChange,
  onOpenAIReasoningEffortChange,
  onOpenAIToolModeChange,
  onLanguageModeChange,
  onGeminiConfigChange,
  onDefaultVoiceChange,
  onEnableConsentChange,
  onInputUserIdentifierChange,
  onEnableSessionPromptChange,
}: Props) => {
  const { t } = useTranslation();

  // 所有已支持的 provider，来自共享常量；新增 provider 时自动渲染出对应的音色选择项
  const providers = Object.keys(REALTIME_VOICE_OPTIONS) as RealtimeProvider[];

  const accessModes: Array<{
    value: boolean;
    icon: React.ReactNode;
    label: string;
    description: string;
  }> = [
    {
      value: false,
      icon: <IconWorld size={20} />,
      label: t('settings.realtime.access_control.mode.public'),
      description: t('settings.realtime.access_control.mode.public_desc'),
    },
    {
      value: true,
      icon: <IconUsers size={20} />,
      label: t('settings.realtime.access_control.mode.authenticated'),
      description: t('settings.realtime.access_control.mode.authenticated_desc'),
    },
  ];

  // 验证邮箱或域名格式
  const validateEmailOrDomain = (value: string): boolean => {
    // 域名格式：@example.com
    const domainPattern = /^@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    // 完整邮箱格式：user@example.com
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    return domainPattern.test(value) || emailPattern.test(value);
  };

  // 处理邮箱列表变更
  const handleEmailChange = (newEmails: string[]) => {
    const lastEmail = newEmails[newEmails.length - 1];
    if (lastEmail && !validateEmailOrDomain(lastEmail)) {
      showNotification({
        message: t('settings.realtime.access_control.email_error'),
        color: 'red',
      });
      return;
    }
    form.setFieldValue('realtime_access_control_allow_email_list', newEmails);
    onValueChange(form.values.enable_realtime_access_control, newEmails);
  };

  return (
    <div
      className="relative rounded-xl bg-white flex-1 p-6"
      style={{ boxShadow: '0px 10px 60px 0px rgba(226, 236, 249, 0.50)' }}
    >
      <Stack gap="lg">
        {/* 模型选择 */}
        <Stack gap={5}>
          <Text size="sm" fw={600}>
            {t('settings.realtime.model.title')}
          </Text>
          <Text size="xs" c="gray.6">
            {t('settings.realtime.model.subtitle')}
          </Text>
        </Stack>
        <Select
          value={form.values.realtime_model}
          onChange={onProviderChange}
          data={[
            { value: 'gemini', label: 'Gemini' },
            { value: 'openai', label: 'OpenAI' },
          ]}
          w={200}
        />

        {/* OpenAI 专属配置 */}
        <Transition
          mounted={form.values.realtime_model === 'openai'}
          transition="scale-y"
          duration={300}
          timingFunction="ease"
        >
          {(styles) => (
            <Box style={styles} className="bg-gray-50 rounded-lg p-5">
              <Stack gap="md">
                <Stack gap={5}>
                  <Text size="sm" fw={600}>
                    {t('settings.realtime.openai_model.title')}
                  </Text>
                  <Text size="xs" c="gray.6">
                    {t('settings.realtime.openai_model.subtitle')}
                  </Text>
                </Stack>
                <Select
                  value={form.values.openai_realtime_model}
                  onChange={onOpenAIModelChange}
                  data={OPENAI_REALTIME_MODELS}
                  w={240}
                />

                {/* gpt-live-1 声音模型提示词：live 模型只拿这一份（人设/说话方式/发音/语言）；业务规则在 Realtime prompt（委任后端处理） */}
                {OPENAI_LIVE_MODELS.has(form.values.openai_realtime_model) && (
                  <>
                    <Stack gap={5}>
                      <Group justify="space-between" align="center">
                        <Text size="sm" fw={600}>
                          {t('settings.realtime.openai_live_voice_prompt.title')}
                        </Text>
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          onClick={() => onOpenAILiveVoicePromptChange(DEFAULT_OPENAI_LIVE_VOICE_PROMPT)}
                        >
                          {t('settings.realtime.openai_live_voice_prompt.load_template')}
                        </Button>
                      </Group>
                      <Text size="xs" c="gray.6">
                        {t('settings.realtime.openai_live_voice_prompt.subtitle')}
                      </Text>
                    </Stack>
                    <Textarea
                      autosize
                      minRows={5}
                      maxRows={16}
                      maxLength={OPENAI_LIVE_VOICE_PROMPT_MAX_LEN}
                      placeholder={t('settings.realtime.openai_live_voice_prompt.placeholder')}
                      value={form.values.openai_live_voice_prompt}
                      onChange={(e) => form.setFieldValue('openai_live_voice_prompt', e.currentTarget.value)}
                      onBlur={() => onOpenAILiveVoicePromptChange(form.values.openai_live_voice_prompt)}
                    />
                  </>
                )}

                {/* Reasoning effort：仅 reasoning 系列模型（gpt-realtime-2 起）支持；gpt-live-1 无 effort，自动隐藏 */}
                {OPENAI_REALTIME_REASONING_MODELS.has(form.values.openai_realtime_model) && (
                  <>
                    <Stack gap={5}>
                      <Text size="sm" fw={600}>
                        {t('settings.realtime.openai_reasoning_effort.title')}
                      </Text>
                      <Text size="xs" c="gray.6">
                        {t('settings.realtime.openai_reasoning_effort.subtitle')}
                      </Text>
                    </Stack>
                    <Select
                      value={form.values.openai_realtime_reasoning_effort}
                      onChange={onOpenAIReasoningEffortChange}
                      data={OPENAI_REASONING_EFFORT_VALUES.map((v) => ({
                        value: v,
                        label: t(`settings.realtime.openai_reasoning_effort.${v}`),
                      }))}
                      w={240}
                    />
                  </>
                )}

                {/* 工具编排模式：classic（委派 catalog-agent）/ direct（voice agent 直连 MCP） */}
                <Stack gap={5}>
                  <Text size="sm" fw={600}>
                    {t('settings.realtime.openai_tool_mode.title')}
                  </Text>
                  <Text size="xs" c="gray.6">
                    {t('settings.realtime.openai_tool_mode.subtitle')}
                  </Text>
                </Stack>
                <Select
                  value={form.values.openai_realtime_tool_mode}
                  onChange={onOpenAIToolModeChange}
                  data={OPENAI_REALTIME_TOOL_MODES.map((v) => ({
                    value: v,
                    label: t(`settings.realtime.openai_tool_mode.${v}`),
                  }))}
                  w={240}
                />

                {/* 応答言語モード：fixed（画面で選択した言語で固定）/ auto（各ターンのユーザー言語に合わせる）。OpenAI 線のみ */}
                <Stack gap={5}>
                  <Text size="sm" fw={600}>
                    {t('settings.realtime.language_mode.title')}
                  </Text>
                  <Text size="xs" c="gray.6">
                    {t('settings.realtime.language_mode.subtitle')}
                  </Text>
                </Stack>
                <Select
                  value={form.values.realtime_language_mode}
                  onChange={onLanguageModeChange}
                  data={REALTIME_LANGUAGE_MODES.map((v) => ({
                    value: v,
                    label: t(`settings.realtime.language_mode.${v}`),
                  }))}
                  w={240}
                />
              </Stack>
            </Box>
          )}
        </Transition>

        {/* Gemini 专属配置 */}
        <Transition
          mounted={form.values.realtime_model === 'gemini'}
          transition="scale-y"
          duration={300}
          timingFunction="ease"
        >
          {(styles) => (
            <Box style={styles} className="bg-gray-50 rounded-lg p-5">
              <Stack gap="md">
                {/* Gemini 模型版本 */}
                <Stack gap={5}>
                  <Text size="sm" fw={600}>
                    {t('settings.realtime.gemini_model.title')}
                  </Text>
                  <Text size="xs" c="gray.6">
                    {t('settings.realtime.gemini_model.subtitle')}
                  </Text>
                </Stack>
                <Select
                  value={form.values.gemini_realtime_model}
                  onChange={(value) => value && onGeminiConfigChange('gemini_realtime_model', value)}
                  data={GEMINI_REALTIME_MODELS}
                  w={240}
                />

                {/* 思考程度：仅支持 thinkingLevel 的模型（gemini-3.8-live 为固定档，不显示） */}
                {GEMINI_THINKING_MODELS.has(form.values.gemini_realtime_model) && (
                  <>
                    <Stack gap={5}>
                      <Text size="sm" fw={600}>
                        {t('settings.realtime.voice_thinking_level.title')}
                      </Text>
                      <Text size="xs" c="gray.6">
                        {t('settings.realtime.voice_thinking_level.subtitle')}
                      </Text>
                    </Stack>
                    <Select
                      value={form.values.voice_thinking_level}
                      onChange={(value) => value && onGeminiConfigChange('voice_thinking_level', value)}
                      data={[
                        { value: 'minimal', label: t('settings.realtime.voice_thinking_level.minimal') },
                        { value: 'low', label: t('settings.realtime.voice_thinking_level.low') },
                        { value: 'medium', label: t('settings.realtime.voice_thinking_level.medium') },
                        { value: 'high', label: t('settings.realtime.voice_thinking_level.high') },
                      ]}
                      w={200}
                    />
                  </>
                )}

                {/* 工具响应模式 - 仅非生产环境显示 */}
                {isNonProduction && (
                  <>
                    <Stack gap={5}>
                      <Group gap={8}>
                        <Text size="sm" fw={600}>
                          {t('settings.realtime.voice_tool_mode.title')}
                        </Text>
                        <BetaBadge />
                      </Group>
                      <Text size="xs" c="gray.6">
                        {t('settings.realtime.voice_tool_mode.subtitle')}
                      </Text>
                    </Stack>
                    <Select
                      value={form.values.voice_tool_mode}
                      onChange={(value) => value && onGeminiConfigChange('voice_tool_mode', value)}
                      data={[
                        { value: 'classic', label: t('settings.realtime.voice_tool_mode.classic') },
                        { value: 'stream', label: t('settings.realtime.voice_tool_mode.stream') },
                      ]}
                      w={200}
                    />
                  </>
                )}
              </Stack>
            </Box>
          )}
        </Transition>

        {/* 各 provider 的默认音色 */}
        <Stack gap={5}>
          <Text size="sm" fw={600}>
            {t('settings.realtime.default_voice.title')}
          </Text>
          <Text size="xs" c="gray.6">
            {t('settings.realtime.default_voice.subtitle')}
          </Text>
        </Stack>
        <Box className="bg-gray-50 rounded-lg p-5">
          <Stack gap="sm">
            {providers.map((provider) => {
              const current = form.values.realtime_default_voices?.[provider] || REALTIME_DEFAULT_VOICE_MAP[provider];
              return (
                <Group key={provider} gap="md" align="center" wrap="nowrap">
                  <Text size="sm" fw={500} w={100}>
                    {REALTIME_PROVIDER_LABEL[provider] || provider}
                  </Text>
                  <VoiceSelector
                    provider={provider}
                    model={provider === 'openai' ? form.values.openai_realtime_model : undefined}
                    value={current}
                    onChange={(voice) => onDefaultVoiceChange(provider, voice)}
                    width={180}
                  />
                </Group>
              );
            })}
          </Stack>
        </Box>

        <Divider />

        {/* 标题和说明 */}
        <Stack gap={5}>
          <Text size="sm" fw={600}>
            {t('settings.realtime.access_control.title')}
          </Text>
          <Text size="xs" c="gray.6">
            {t('settings.realtime.access_control.subtitle')}
          </Text>
        </Stack>

        {/* 访问模式选择 */}
        <Box className="bg-gray-50 rounded-lg p-5">
          <Stack gap="md">
            <Radio.Group
              value={String(form.values.enable_realtime_access_control)}
              onChange={(value) => {
                const boolValue = value === 'true';
                form.setFieldValue('enable_realtime_access_control', boolValue);
                onValueChange(boolValue);
              }}
            >
              <Stack gap="sm">
                {accessModes.map((mode) => (
                  <Box
                    key={String(mode.value)}
                    className={`rounded-lg border transition-all ${
                      form.values.enable_realtime_access_control === mode.value
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <Box
                      className="p-4 cursor-pointer"
                      onClick={() => {
                        form.setFieldValue('enable_realtime_access_control', mode.value);
                        onValueChange(mode.value);
                      }}
                    >
                      <Group align="flex-start" wrap="nowrap">
                        <Radio
                          value={String(mode.value)}
                          styles={{
                            radio: { cursor: 'pointer' },
                          }}
                        />
                        <Stack gap={4} className="flex-1">
                          <Group gap={8}>
                            {mode.icon}
                            <Text size="sm" fw={500}>
                              {mode.label}
                            </Text>
                          </Group>
                          <Text size="xs" c="gray.6">
                            {mode.description}
                          </Text>
                        </Stack>
                      </Group>
                    </Box>

                    {/* 当选择登录用户时，在选项框内显示邮箱白名单输入 */}
                    {mode.value === true && (
                      <Transition
                        mounted={form.values.enable_realtime_access_control}
                        transition="scale-y"
                        duration={300}
                        timingFunction="ease"
                      >
                        {(styles) => (
                          <div style={styles} className="mx-4 mb-4 p-4 bg-white rounded-lg border border-gray-200">
                            <TagsInput
                              label={
                                <Text size="sm" fw={500} mb={4}>
                                  {t('settings.realtime.access_control.allowed_emails')}
                                </Text>
                              }
                              placeholder={t('settings.realtime.access_control.email_placeholder')}
                              description={
                                <Text size="xs" c="dimmed" mt={6}>
                                  {t('settings.realtime.access_control.email_list_tip')}
                                </Text>
                              }
                              value={form.values.realtime_access_control_allow_email_list}
                              onChange={handleEmailChange}
                              withScrollArea
                              size="sm"
                              styles={() => ({
                                input: {
                                  borderRadius: '6px',
                                },
                                pillsList: {
                                  maxHeight: '180px',
                                  overflowY: 'auto',
                                  gap: '6px',
                                },
                                pill: {
                                  backgroundColor: 'var(--mantine-color-violet-1)',
                                  color: 'var(--mantine-color-violet-7)',
                                  fontWeight: 500,
                                },
                              })}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}
                      </Transition>
                    )}
                  </Box>
                ))}
              </Stack>
            </Radio.Group>
          </Stack>
        </Box>

        <Divider />

        {/* Realtime 打开时是否要求输入昵称 */}
        <Stack gap={5}>
          <Text size="sm" fw={600}>
            {t('settings.realtime.nickname.title')}
          </Text>
          <Text size="xs" c="gray.6">
            {t('settings.realtime.nickname.subtitle')}
          </Text>
        </Stack>
        <Box className="bg-gray-50 rounded-lg p-5">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={4} className="flex-1">
              <Text size="sm" fw={600}>
                {t('settings.realtime.nickname.switch_title')}
              </Text>
              <Text size="xs" c="gray.6">
                {t('settings.realtime.nickname.switch_desc')}
              </Text>
            </Stack>
            <Switch
              checked={form.values.realtime_input_user_identifier}
              onChange={(e) => onInputUserIdentifierChange(e.currentTarget.checked)}
            />
          </Group>
        </Box>

        <Divider />

        {/* Realtime 使用前同意声明 */}
        <Stack gap={5}>
          <Text size="sm" fw={600}>
            {t('settings.realtime.consent.title')}
          </Text>
          <Text size="xs" c="gray.6">
            {t('settings.realtime.consent.subtitle')}
          </Text>
        </Stack>
        <Box className="bg-gray-50 rounded-lg p-5">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={4} className="flex-1">
              <Text size="sm" fw={600}>
                {t('settings.realtime.consent.switch_title')}
              </Text>
              <Text size="xs" c="gray.6">
                {t('settings.realtime.consent.switch_desc')}
              </Text>
              <Text size="xs" c="violet.6">
                {t('consent.settings.content_edit_hint')}
              </Text>
            </Stack>
            <Switch
              checked={form.values.enable_realtime_consent}
              onChange={(e) => onEnableConsentChange(e.currentTarget.checked)}
            />
          </Group>
        </Box>

        <Divider />

        {/* 会话级 prompt：允许用户在 Realtime 页为本次通话临时设置 prompt */}
        <Stack gap={5}>
          <Text size="sm" fw={600}>
            {t('settings.realtime.session_prompt.title')}
          </Text>
          <Text size="xs" c="gray.6">
            {t('settings.realtime.session_prompt.subtitle')}
          </Text>
        </Stack>
        <Box className="bg-gray-50 rounded-lg p-5">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={4} className="flex-1">
              <Text size="sm" fw={600}>
                {t('settings.realtime.session_prompt.switch_title')}
              </Text>
              <Text size="xs" c="gray.6">
                {t('settings.realtime.session_prompt.switch_desc')}
              </Text>
            </Stack>
            <Switch
              checked={form.values.enable_realtime_session_prompt}
              onChange={(e) => onEnableSessionPromptChange(e.currentTarget.checked)}
            />
          </Group>
        </Box>
      </Stack>
    </div>
  );
};
